import * as React from 'react';
import * as ReactDOMServer from 'react-dom/server';

interface ModalContentProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {}

const modalContentFactory = (className: string) => (props: ModalContentProps): React.ReactElement<ModalContentProps> => {
  // tslint:disable-next-line:prefer-const
  let { className: modalContentClassName, children, ...otherProps } = props;
  modalContentClassName = [props.className, className].join(' ');
  return (
    <div className={modalContentClassName} {...otherProps}>
      {children}
    </div>
  );
};

export type ModalHeaderProps = ModalContentProps;
export const ModalHeader = modalContentFactory('modal-header');

export type ModalBodyProps = ModalContentProps;
export const ModalBody = modalContentFactory('modal-body');

export type ModalFooterProps = ModalContentProps;
export const ModalFooter = modalContentFactory('modal-footer');

export interface ModalDialogProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  id: string;
  size?: 'large' | 'small' | 'auto';
  centered?: boolean;
  show: boolean;
  escape?: boolean;
  onShown?: (elModal: HTMLElement) => void;
  onHidden?: (elModal: HTMLElement) => void;
}

interface ModalDialogState {
  elModal: HTMLElement | null;
  modalDialogId: string;
  isShown: boolean | undefined;
}

class ModalDialog extends React.PureComponent<ModalDialogProps, ModalDialogState> {
  public static getDerivedStateFromProps(
    nextProps: Readonly<ModalDialogProps>,
    prevState: ModalDialogState
  ): Partial<ModalDialogState> | null {
    if (nextProps.show !== prevState.isShown) {
      return { isShown: nextProps.show };
    }
    return null;
  }

  constructor(props: ModalDialogProps) {
    super(props);

    const trimmedId = typeof this.props.id === 'string' && this.props.id.trim();
    if (!trimmedId) {
      throw new Error('Property <id> is required.');
    }

    this.state = {
      elModal: document.getElementById('modalAppCommon'),
      modalDialogId: trimmedId,
      isShown: undefined
    };

    this.onDismissClick = this.onDismissClick.bind(this);
    this.onDocumentFocus = this.onDocumentFocus.bind(this);
    this.onEscapeKeyDown = this.onEscapeKeyDown.bind(this);
  }

  public componentWillUnmount(): void {
    const { elModal } = this.state;
    const elDismiss = elModal === null ? [] : Array.from(elModal.querySelectorAll<HTMLElement>('[data-dismiss="modal"]'));
    elDismiss.forEach((el) => el.removeEventListener('click', this.onDismissClick));
    window.removeEventListener('keydown', this.onEscapeKeyDown);
    document.removeEventListener('focus', this.onDocumentFocus);
  }

  public render(): React.ReactNode {
    const { elModal } = this.state;
    if (elModal !== null) {
      const elModalDialog = elModal.querySelector<HTMLDivElement>('.modal-dialog');
      if (elModalDialog != null) {
        if (this.state.isShown) {
          this.hideAnyOpenModal();
          this.modalDialogApplyOrRemoveProperties(elModalDialog, true);
          elModalDialog.innerHTML = ReactDOMServer.renderToStaticMarkup(<div className="modal-content">{this.props.children}</div>);
          this.showModal();
        } else if (elModalDialog.id === this.state.modalDialogId) {
          this.hideAnyOpenModal();
          elModalDialog.innerHTML = '';
          this.modalDialogApplyOrRemoveProperties(elModalDialog, false);
        }
      }
    }
    return null;
  }

  public onDismissClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.hideAnyOpenModal();
  }

  public onDocumentFocus(event: FocusEvent): void {
    const { elModal } = this.state;
    if (elModal === null) {
      return;
    }

    if (document !== event.target && elModal !== event.target) {
      const allDescendantElements = Array.from(elModal.getElementsByTagName('*'));
      if (!allDescendantElements.some((el) => el === event.target)) {
        elModal.focus();
      }
    }
  }

  public onEscapeKeyDown(event: KeyboardEvent): void {
    if (this.props.escape === false) {
      return;
    }

    // ESC key
    if (event.which === 27) {
      event.preventDefault();
      this.hideAnyOpenModal();
    }
  }

  private modalDialogApplyOrRemoveProperties(elModalDialog: HTMLDivElement, apply: boolean): void {
    if (apply) {
      elModalDialog.setAttribute('id', this.state.modalDialogId);
    } else {
      elModalDialog.removeAttribute('id');
    }

    if (this.props.size === 'large' || this.props.size === 'small') {
      if (apply) {
        elModalDialog.classList.add(this.props.size === 'large' ? 'modal-lg' : 'modal-sm');
      } else {
        elModalDialog.classList.remove(this.props.size === 'large' ? 'modal-lg' : 'modal-sm');
      }
    }

    if (this.props.centered === true) {
      if (apply) {
        elModalDialog.classList.add('modal-dialog-centered');
      } else {
        elModalDialog.classList.remove('modal-dialog-centered');
      }
    }
  }

  private showModal(): void {
    const { elModal } = this.state;
    if (elModal === null || document.body.classList.contains('modal-open')) {
      return;
    }

    document.body.classList.add('modal-open');
    this.showBackdrop(() => {
      elModal.style.display = 'block';
      elModal.removeAttribute('aria-hidden');
      elModal.scrollTop = 0;
      elModal.classList.add('show');

      const elDismiss = Array.from(elModal.querySelectorAll<HTMLElement>('[data-dismiss="modal"]'));
      elDismiss.forEach((el) => el.addEventListener('click', this.onDismissClick, { once: true }));
      window.addEventListener('keydown', this.onEscapeKeyDown, { once: true });
      document.addEventListener('focus', this.onDocumentFocus, { capture: true });
      elModal.focus();

      if (typeof this.props.onShown === 'function') {
        this.props.onShown(elModal);
      }
    });
  }

  private hideAnyOpenModal(): void {
    const { elModal } = this.state;
    if (elModal === null || !document.body.classList.contains('modal-open')) {
      return;
    }

    elModal.classList.remove('show');
    elModal.style.display = 'none';
    elModal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('modal-open');
    this.removeBackdrop();

    const elDismiss = Array.from(elModal.querySelectorAll<HTMLElement>('[data-dismiss="modal"]'));
    elDismiss.forEach((el) => el.removeEventListener('click', this.onDismissClick));
    window.removeEventListener('keydown', this.onEscapeKeyDown);
    document.removeEventListener('focus', this.onDocumentFocus);

    if (typeof this.props.onHidden === 'function') {
      const elModalDialog = elModal.querySelector<HTMLDivElement>('.modal-dialog');
      if (elModalDialog != null && elModalDialog.id === this.state.modalDialogId) {
        this.props.onHidden(elModal);
      }
    }
  }

  private showBackdrop(callback: () => void): void {
    const elBackdrop = document.createElement('div');
    elBackdrop.className = 'modal-backdrop fade show';
    document.body.appendChild(elBackdrop);
    callback();
  }

  private removeBackdrop() {
    const elBackdrop = document.querySelector<HTMLDivElement>('.modal-backdrop');
    if (elBackdrop != null) {
      document.body.removeChild(elBackdrop);
    }
  }
}

export default ModalDialog;
